/**
 * Canonical route matcher for Option A microfrontend stitching.
 *
 * www-sbi acts as the edge router and forwards stitched routes to
 * their dedicated workers through service bindings.
 */

export type StitchedMicrofrontendTarget = 'astro' | 'briefs';

/**
 * The service binding of each stitched target. #6410 (EPIC #6400 S3): briefs-sbi serves the
 * public brief reader. It has no route of its own, so this binding is its only way in.
 */
export const STITCHED_TARGET_BINDINGS = {
  astro: 'ASTRO_APP',
  briefs: 'BRIEFS_APP',
} as const satisfies Record<StitchedMicrofrontendTarget, string>;

// admin-sbi retired (Smallbizinnovation/app-sbsv2#5239): the former
// `/admin`, `/admin/*` and `/api/analytics/*` stitch rules to ADMIN_APP were
// removed when the CMS moved to app.smallbiz.systems/gtm/website. `/admin*` is
// now handled by an explicit 302 redirect in index.ts (before this dispatch);
// `/api/analytics/*` had no consumer other than the retired admin SPA.
const MICROFRONTEND_ROUTE_RULES: Array<{
  target: StitchedMicrofrontendTarget;
  exact?: string;
  prefix?: string;
}> = [
  { target: 'astro', exact: '/glossary' },
  { target: 'astro', prefix: '/glossary/' },
  { target: 'astro', exact: '/tips' },
  { target: 'astro', prefix: '/tips/' },
  // #1478: toolbox migrated from the www React SPA to astro static prerender.
  // Rollback = remove these two rules → traffic returns to the SPA route.
  { target: 'astro', exact: '/toolbox' },
  { target: 'astro', prefix: '/toolbox/' },
  { target: 'astro', exact: '/sitemap-glossary-tips.xml' },
  // Astro-compiled static assets (CSS chunks, JS modules). These live in astro-sbi's
  // dist/_astro/ bundle. Without this rule, www-sbi's SPA fallback intercepts them
  // and returns index.html (text/html), breaking all styles and JS on /glossary + /tips.
  { target: 'astro', prefix: '/_astro/' },
  // #6410: the public brief reader (unlock form, brief, expired and not-found pages).
  { target: 'briefs', prefix: '/briefs/' },
];

export function getStitchedMicrofrontendTarget(
  pathname: string
): StitchedMicrofrontendTarget | null {
  for (const rule of MICROFRONTEND_ROUTE_RULES) {
    if (rule.exact && pathname === rule.exact) {
      return rule.target;
    }
    if (rule.prefix && pathname.startsWith(rule.prefix)) {
      return rule.target;
    }
  }

  return null;
}

export function isStitchedMicrofrontendPath(pathname: string): boolean {
  return getStitchedMicrofrontendTarget(pathname) !== null;
}
